import React from 'react';
import { Action, withInject } from '@piwikpro/platform';
import {
  Stack,
  Icon,
  Tooltip,
  Text,
  withTranslation,
} from '@piwikpro/ui-components';
import { TrackerSettings, TrackerSettingsResponse } from '../../../TrackerSettings';

export interface ExcludeCrawlersProps {
  appId: string
  trackerSettings: TrackerSettings
  globalTrackerSettings: TrackerSettingsResponse
  appTrackerSettings: TrackerSettingsResponse
  t: (key: string) => string
}

export interface ExcludeCrawlersState {
  useGlobals: boolean
}

class ExcludeCrawlers extends React.Component<ExcludeCrawlersProps, ExcludeCrawlersState> {
  static readonly fieldName = 'exclude_crawlers';

  constructor(props) {
    super(props);
    const { appTrackerSettings } = props;

    this.state = {
      useGlobals: appTrackerSettings.attributes?.[ExcludeCrawlers.fieldName] === undefined,
    };

    this.handleSubmitSwitcher = this.handleSubmitSwitcher.bind(this);
    this.handleSubmitCheckbox = this.handleSubmitCheckbox.bind(this);
  }

  async handleSubmitSwitcher(
    values,
    { setFieldValue, setSubmitting }: {
      setSubmitting: Function
      setFieldValue: Function
    },
  ) {
    const { trackerSettings, appId } = this.props;

    try {
      await trackerSettings.updateSettingsForApp(appId, values);
    } catch (err) {
      setFieldValue(ExcludeCrawlers.fieldName, !values[ExcludeCrawlers.fieldName], false);
    }

    setSubmitting(false);
  }

  async handleSubmitCheckbox(
    values,
    { setFieldValue, setSubmitting }: {
      setSubmitting: Function
      setFieldValue: Function
    },
  ) {
    const {
      trackerSettings,
      globalTrackerSettings,
      appId,
    } = this.props;

    try {
      if (values.exclude_crawlers_global) {
        await trackerSettings.deleteSettingsForApp(appId, ExcludeCrawlers.fieldName);
      } else {
        await trackerSettings.updateSettingsForApp(appId, {
          [ExcludeCrawlers.fieldName]:
            globalTrackerSettings.attributes[ExcludeCrawlers.fieldName],
        });

        setFieldValue(
          ExcludeCrawlers.fieldName,
          globalTrackerSettings.attributes[ExcludeCrawlers.fieldName],
          false,
        );
      }
    } catch (err) {
      setFieldValue('exclude_crawlers_global', !values.exclude_crawlers_global, false);
      this.setState({
        useGlobals: !values.exclude_crawlers_global,
      });

      setSubmitting(false);

      return;
    }

    this.setState({
      useGlobals: values.exclude_crawlers_global,
    });

    setSubmitting(false);
  }

  render() {
    const {
      appTrackerSettings,
      globalTrackerSettings,
      t,
    } = this.props;
    const { useGlobals } = this.state;

    return (
      <Stack vertical>
        <Stack.Item fill>
          <Stack wrap verticalAlignment="center">
            <Stack.Item fill>
              <Stack vertical spacing="narrow">
                <Stack.Item>
                  <Text as="h2" size={20} weight={700} wrapType="noWrapAndCutText" dataId="Header.medium">{t('apps.details.settings.tracking.exclude-crawlers.title')}</Text>
                </Stack.Item>
                <Stack.Item>
                  <Text as="p">{t('apps.details.settings.tracking.exclude-crawlers.description')}</Text>
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item>
              <Action.AutoSavedSwitcherControl
                id="app-settings-tracking-exclude-crawlers"
                initialValue={
                  useGlobals
                    ? globalTrackerSettings.attributes[ExcludeCrawlers.fieldName]
                    : appTrackerSettings.attributes[ExcludeCrawlers.fieldName]
                }
                label={t('apps.details.settings.tracking.exclude-crawlers.title')}
                info={t('apps.details.settings.tracking.exclude-crawlers.description')}
                name={ExcludeCrawlers.fieldName}
                onSubmit={this.handleSubmitSwitcher}
                disabled={useGlobals}
                disabledActionTooltipText={t('app.details.settings.general.use-global-settings.block-reason')}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
        <Stack.Item>
          <Stack spacing="narrow">
            <Stack.Item>
              <Action.AutoSavedCheckboxControl
                id="app-settings-exclude-crawlers-use-globals"
                onSubmit={this.handleSubmitCheckbox}
                initialValue={useGlobals}
                label={t('app.details.settings.general.use-global-settings.label')}
                name="exclude_crawlers_global"
              />
            </Stack.Item>
            <Stack.Item>
              <Tooltip content={t('app.details.settings.general.use-global-settings.tooltip') as any}>
                <div>
                  <Icon name="info-circle" />
                </div>
              </Tooltip>
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    );
  }
}

export default withInject({
  trackerSettings: 'TrackerSettingsCrate.tracker',
})(
  withTranslation(['administration'])(ExcludeCrawlers as any),
);
